import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, Link } from "react-router-dom";
import { list } from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { ExportButtons } from "@/components/ExportButtons";
import { StatusPill, PriorityPill } from "@/components/StatusPill";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { FileText, MapPin, User, Truck, Building2, Calendar, ChevronDown, ChevronRight, FileSignature, Package, Weight, Boxes } from "lucide-react";
import { toast } from "sonner";

const fmt = (v) => (v ? String(v).slice(0, 10) : "—");
const dt = (v) => (v ? String(v).slice(0, 16).replace("T", " ") : "—");

export default function WorkOrders() {
  const nav = useNavigate();
  const { data = [], isLoading } = useQuery({ queryKey: ["requisitions"], queryFn: () => list("requisitions") });
  const orders = data.filter((r) => r.work_order_no);
  const [selectedId, setSelectedId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const selected = orders.find((o) => o.id === selectedId);
  const toggleSelect = (id) => setSelectedId((cur) => (cur === id ? null : id));
  const toggleExpand = (id) => setExpandedId((cur) => (cur === id ? null : id));

  const generateBol = () => {
    if (!selectedId) { toast.error("Select a work order first"); return; }
    toast.success(`Generating Bill of Lading for ${selected.work_order_no}`);
    nav(`/wo-doc/${selectedId}/bol`);
  };

  return (
    <Layout>
      <PageHeader title="Work Orders" subtitle="Select a work order to expand details & auto-generate its Bill of Lading">
        <ExportButtons collection="work_orders" />
      </PageHeader>
      <div className="p-8 animate-fade-up">
        {/* action bar */}
        <div className="flex items-center justify-between gap-3 mb-4 rounded-md border border-border/60 bg-white px-4 py-2.5">
          <div className="text-sm text-muted-foreground" data-testid="wo-selection-label">
            {selected ? <>Selected: <span className="font-semibold text-neutral-900 font-mono-data">{selected.work_order_no}</span> · {selected.project_name || "—"}</> : "No work order selected"}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={!selectedId} data-testid="print-selected-wo-btn"
              onClick={() => nav(`/wo-doc/${selectedId}/wo`)} className="gap-2">
              <FileText className="h-4 w-4" /> Print WO
            </Button>
            <Button size="sm" disabled={!selectedId} data-testid="generate-bol-btn"
              onClick={generateBol} className="gap-2 bg-[#0055FF] hover:bg-[#0044CC]">
              <FileSignature className="h-4 w-4" /> Auto-generate BOL
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="text-muted-foreground">Loading…</div>
        ) : orders.length === 0 ? (
          <Card className="p-12 text-center border-dashed border-border/60 shadow-none">
            <FileText className="h-10 w-10 mx-auto text-neutral-300 mb-3" />
            <p className="text-muted-foreground">No work orders yet. Approve or schedule a requisition in the Master Log to auto-generate a work order.</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {orders.map((r) => {
              const isSel = r.id === selectedId;
              const isExp = r.id === expandedId;
              return (
                <Card key={r.id} data-testid={`wo-card-${r.id}`}
                  className={`border-border/60 shadow-none transition-all ${isSel ? "ring-2 ring-[#0055FF] bg-blue-50/30" : "hover:shadow-md bg-white"}`}>
                  <div className="flex items-center gap-4 p-4">
                    <Checkbox checked={isSel} onCheckedChange={() => toggleSelect(r.id)} data-testid={`select-wo-${r.id}`} className="shrink-0" />
                    <button className="flex-1 min-w-0 text-left flex items-center gap-4" data-testid={`expand-wo-${r.id}`} onClick={() => toggleExpand(r.id)}>
                      {isExp ? <ChevronDown className="h-4 w-4 text-neutral-400 shrink-0" /> : <ChevronRight className="h-4 w-4 text-neutral-400 shrink-0" />}
                      <div className="w-28 shrink-0">
                        <div className="font-display font-extrabold text-base leading-none">{r.work_order_no}</div>
                        <div className="font-mono-data text-[11px] text-muted-foreground mt-0.5">{r.requisition_no}</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{r.project_name || "—"}</div>
                        <div className="text-xs text-muted-foreground truncate flex items-center gap-1"><MapPin className="h-3 w-3" />{r.pickup_location || "?"} → {r.delivery_location || "?"}</div>
                      </div>
                      <div className="hidden sm:block text-xs text-muted-foreground truncate w-40">{r.asset_source === "Internal Asset" ? (r.driver_name || "Unassigned") : (r.third_party_carrier || "Unassigned")}</div>
                    </button>
                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      <StatusPill value={r.status} />
                      <PriorityPill value={r.priority} />
                    </div>
                    <div className="w-20 text-right font-mono-data text-sm font-semibold shrink-0">{r.estimated_cost ? `$${Number(r.estimated_cost).toLocaleString()}` : "—"}</div>
                  </div>

                  {isExp && (
                    <div className="px-5 pb-5 pt-1 border-t border-border/60 bg-neutral-50/40" data-testid={`wo-detail-${r.id}`}>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-3 text-sm mt-3">
                        <Detail icon={Calendar} label="Est. Start" value={dt(r.estimated_start)} />
                        <Detail icon={Calendar} label="Est. End" value={dt(r.estimated_end)} />
                        <Detail icon={FileText} label="Est. Hours" value={r.estimated_time_hrs ?? "—"} />
                        {r.asset_source === "Internal Asset" ? (
                          <>
                            <Detail icon={User} label="Driver" value={r.driver_name || "Unassigned"} />
                            <Detail icon={Truck} label="Truck / Trailer" value={[r.truck_unit_number && `${r.truck_type} #${r.truck_unit_number}`, r.trailer_number && `${r.trailer_type} #${r.trailer_number}`].filter(Boolean).join(" + ") || "—"} />
                          </>
                        ) : (
                          <Detail icon={Building2} label="Carrier" value={r.third_party_carrier || "Unassigned"} />
                        )}
                        <Detail icon={Boxes} label="Freight Class" value={r.freight_class || "50"} />
                        <Detail icon={Weight} label="Weight" value={r.weight_lbs ? `${r.weight_lbs} lb` : (r.estimated_weight || "—")} />
                        <Detail icon={Package} label="Load" value={r.load_description || "—"} />
                        <Detail icon={FileText} label="Client" value={r.client_name || "—"} />
                      </div>
                      {r.special_instructions && (
                        <div className="mt-3 text-xs bg-amber-50 border border-amber-200 rounded px-3 py-2 text-amber-800">⚠ {r.special_instructions}</div>
                      )}
                      <div className="mt-4 flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" className="gap-2" data-testid={`detail-select-${r.id}`} onClick={() => setSelectedId(r.id)}>
                          <FileSignature className="h-3.5 w-3.5" /> {isSel ? "Selected" : "Select for BOL"}
                        </Button>
                        <Link to={`/wo-doc/${r.id}/wo`} data-testid={`print-wo-${r.id}`} className="inline-flex items-center gap-2 text-xs font-semibold rounded-md border border-border/60 px-3 py-1.5 hover:bg-neutral-900 hover:text-white transition-colors">
                          <FileText className="h-3.5 w-3.5" /> Print Work Order
                        </Link>
                        <Link to={`/wo-doc/${r.id}/bol`} data-testid={`print-bol-${r.id}`} className="inline-flex items-center gap-2 text-xs font-semibold rounded-md border border-[#0055FF] text-[#0055FF] px-3 py-1.5 hover:bg-[#0055FF] hover:text-white transition-colors">
                          <FileSignature className="h-3.5 w-3.5" /> Bill of Lading
                        </Link>
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}

const Detail = ({ icon: Icon, label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold flex items-center gap-1"><Icon className="h-3 w-3" />{label}</div>
    <div className="mt-0.5 text-neutral-800">{value}</div>
  </div>
);
